import { useEffect, useRef, useState } from "react";
import { GraduationCap, Loader2, Send, Copy, ClipboardCheck, RotateCcw, Check, Wand2, Undo2, Sparkles, Eye, EyeOff, X, Plus, BellOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg, streamPost } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { emptyNote } from "@/lib/soap";
import { parseCoachFixes } from "@/lib/coachFixes";

const QUICK = [
  "How can I make my Objective section more measurable?",
  "Is medical necessity clear enough for insurance?",
  "What's missing for this service code?",
  "Rewrite my Assessment in clinical language",
];

const FIELDS = Object.keys(emptyNote());
const notePayload = (form) => Object.fromEntries(FIELDS.map((k) => [k, form[k]]));

function Line({ line, fix, idx, undo, onApply, onUndo, streaming, previewing, onPreview, allMode, skipped, onSkip, dismissed, onDismiss }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!previewing) return;
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 120);
    return () => clearTimeout(t);
  }, [previewing]);
  if (!fix) return /\]\s*Required for audit/i.test(line)
    ? <div className="flex items-start gap-1.5 text-amber-800"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" data-testid={`coach-required-${idx}`} /><span>{line}</span></div>
    : <div>{line || "\u00A0"}</div>;
  const blocked = !fix.section && !fix.find;
  const title = fix.instruction ? "This is advice for you, not note text — add the details yourself" : blocked ? "Section unclear — copy it in by hand" : "";
  const off = streaming || blocked || fix.instruction;
  return (
    <div ref={ref} className={`my-1.5 rounded-lg border bg-white p-2.5 flex gap-2 transition-[opacity,border-color] ${previewing ? "flex-col border-amber-400 ring-2 ring-amber-200" : allMode && !undo && !off ? `flex-col ${skipped ? "opacity-50 border-dashed border-slate-300" : "border-amber-300"}` : "items-start border-primary/20"}`}
      data-testid={allMode && skipped ? `coach-line-skipped-${idx}` : undefined}>
      <div className="flex-1 min-w-0">
        <span className="text-slate-800">{line.trim()}</span>
        <div className="mt-1 flex items-center gap-2 text-[11px]">
          {fix.required ? (
            <span className="flex items-center gap-1 font-medium text-amber-700" data-testid={`coach-required-${idx}`}><ShieldCheck className="h-3 w-3" />Required for audit</span>
          ) : dismissed === true ? (
            <span className="text-slate-400" data-testid={`coach-dismissed-${idx}`}>Won't be suggested again</span>
          ) : !streaming && (
            <button onClick={() => onDismiss(fix, idx)} disabled={!!dismissed} className="flex items-center gap-1 text-slate-400 hover:text-red-600 disabled:opacity-50" data-testid={`coach-dismiss-${idx}`}>
              <BellOff className="h-3 w-3" />Don't suggest this
            </button>
          )}
        </div>
      </div>
      {undo ? (
        <div className="flex shrink-0 items-center gap-1">
          <span className="flex items-center text-xs text-emerald-700" data-testid={`coach-applied-${idx}`}><Check className="h-3 w-3 mr-0.5" />Applied</span>
          <Button size="sm" variant="ghost" className="h-7 rounded-full px-2 text-xs" onClick={() => onUndo(idx)} data-testid={`coach-undo-${idx}`}>
            <Undo2 className="h-3 w-3 mr-1" />Undo
          </Button>
        </div>
      ) : allMode && !off ? (
        <Button size="sm" variant={skipped ? "outline" : "ghost"} className="h-7 shrink-0 rounded-full px-3 text-xs self-start" onClick={() => onSkip(idx)} data-testid={`coach-skip-${idx}`}>
          {skipped ? <><Plus className="h-3 w-3 mr-1" />Include</> : <><X className="h-3 w-3 mr-1" />Skip</>}
        </Button>
      ) : (
        <div className="flex shrink-0 items-center gap-1">
          {!off && (
            <Button size="sm" variant="ghost" className="h-7 rounded-full px-2 text-xs" onClick={() => onPreview(fix, idx)} data-testid={`coach-preview-${idx}`}>
              {previewing ? <><EyeOff className="h-3 w-3 mr-1" />Hide</> : <><Eye className="h-3 w-3 mr-1" />Preview</>}
            </Button>
          )}
          <Button size="sm" className="h-7 rounded-full px-3 text-xs" disabled={off} title={title}
            onClick={() => onApply(fix, idx)} data-testid={`coach-apply-${idx}`}>
            <Wand2 className="h-3 w-3 mr-1" />Apply
          </Button>
        </div>
      )}
    </div>
  );
}

function Bubble({ m, i, streaming, undos, setUndos, onApply, applyMany, preview, onPreview, onPreviewAll, onSkip, dismissed, onDismiss }) {
  const coach = m.role === "coach";
  const fixes = coach && !streaming ? parseCoachFixes(m.content) : {};
  const k = (idx) => `${i}:${idx}`;
  const apply = (fix, idx) => { const u = onApply(fix); if (u) setUndos((s) => ({ ...s, [k(idx)]: u })); };
  const undo = (idx) => { if (undos[k(idx)]?.()) setUndos(({ [k(idx)]: _, ...rest }) => rest); };
  const pending = Object.entries(fixes).filter(([idx, f]) => !undos[k(idx)] && !f.instruction && (f.section || f.find)).map(([idx, f]) => [k(idx), f]);
  const allMode = preview?.all && preview.bubble === i;
  const skipped = allMode ? preview.skipped : [];
  const chosen = pending.filter(([key]) => !skipped.includes(key));
  return (
    <div className={`flex ${coach ? "justify-start" : "justify-end"}`} data-testid={`coach-message-${i}`}>
      <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${coach ? "bg-secondary/60 text-slate-800 rounded-tl-sm" : "bg-primary text-white rounded-tr-sm"}`}>
        {!m.content && streaming && <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Coach is reading your note…</span>}
        {coach && m.content
          ? m.content.split("\n").map((line, idx) => (
            <Line key={idx} line={line} idx={idx} fix={fixes[idx]} undo={undos[k(idx)]} onApply={apply} onUndo={undo} streaming={streaming}
              previewing={preview?.single === k(idx)} onPreview={(f, x) => onPreview(f, k(x))}
              allMode={allMode} skipped={skipped.includes(k(idx))} onSkip={(x) => onSkip(k(x))}
              dismissed={dismissed[k(idx)]} onDismiss={(f, x) => onDismiss(f, k(x))} />
          ))
          : m.content}
        {coach && m.content && !streaming && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={() => copyText(m.content, "Coach feedback copied")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary mr-auto" data-testid={`coach-copy-${i}`}>
              <Copy className="h-3 w-3" />Copy
            </button>
            {pending.length > 1 && (
              <Button size="sm" variant="outline" className="h-7 rounded-full px-3 text-xs" onClick={() => onPreviewAll(i, pending)} data-testid={`coach-preview-all-${i}`}>
                {allMode ? <><EyeOff className="h-3 w-3 mr-1" />Hide preview</> : <><Eye className="h-3 w-3 mr-1" />Preview all</>}
              </Button>
            )}
            {pending.length > 1 && (
              <Button size="sm" className="h-7 rounded-full px-3 text-xs" disabled={!chosen.length} onClick={() => applyMany(chosen)} data-testid={`coach-apply-all-${i}`}>
                <Sparkles className="h-3 w-3 mr-1" />{allMode ? `Apply ${chosen.length} selected` : `Apply all ${pending.length} fixes`}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CoachPanel({ form, disabled, onApply, onPreview }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [undos, setUndos] = useState({});
  const [preview, setPreview] = useState(null);
  const [dismissed, setDismissed] = useState({});
  const dismiss = async (fix, key) => {
    if (dismissed[key]) return;
    setDismissed((d) => ({ ...d, [key]: "saving" }));
    const general = (fix.problem || "").replace(/["“][^"”]*["”]/g, "").replace(/\s{2,}/g, " ").replace(/^[\s,;:.\-–—]+/, "").trim();
    try {
      await api.post("/coach/prefs", { section: fix.section || "", problem: (general || fix.problem || fix.replacement).slice(0, 300), example: fix.replacement.slice(0, 400) });
      setDismissed((d) => ({ ...d, [key]: true }));
      toast.success("Got it — the coach won't suggest this again. Manage it in Plan & account.");
    } catch (e) { setDismissed(({ [key]: _, ...d }) => d); toast.error(errMsg(e)); }
  };
  const undosRef = useRef(undos);
  undosRef.current = undos;
  const asList = (entries) => entries.map(([id, fix]) => ({ id, fix }));
  const togglePreview = (fix, key) => {
    if (preview?.single === key) { onPreview(null); setPreview(null); return; }
    const res = onPreview([{ id: key, fix }]);
    setPreview(res ? { single: key, sections: res.sections } : null);
  };
  const showAll = (bubble, entries, skipped) => {
    const res = onPreview(asList(entries.filter(([key]) => !skipped.includes(key))), true);
    setPreview({ all: true, bubble, entries, skipped, sections: res.sections, ok: res.ok.length });
  };
  const previewAll = (bubble, entries) => {
    if (preview?.all && preview.bubble === bubble) { onPreview(null); setPreview(null); return; }
    showAll(bubble, entries, []);
  };
  const toggleSkip = (key) => {
    const s = preview.skipped.includes(key) ? preview.skipped.filter((x) => x !== key) : [...preview.skipped, key];
    showAll(preview.bubble, preview.entries, s);
  };
  const clearPreview = () => { if (preview) { onPreview(null); setPreview(null); } };
  const applyFix = (fix, quiet) => { clearPreview(); return onApply(fix, quiet); };
  const applyMany = (entries) => {
    clearPreview();
    const done = entries.map(([key, f]) => [key, onApply(f, true)]).filter(([, u]) => u);
    if (!done.length) { toast.info("Nothing left to apply — those fixes are already in your note or the original words changed"); return; }
    setUndos((s) => ({ ...s, ...Object.fromEntries(done) }));
    const missed = entries.length - done.length;
    toast.success(`Applied ${done.length} fix${done.length > 1 ? "es" : ""}${missed ? ` · ${missed} skipped` : ""}`, {
      duration: 10000,
      action: { label: "Undo all", onClick: () => undoAll(done) },
    });
  };
  const undoAll = (done) => {
    const live = [...done].reverse().filter(([key, u]) => undosRef.current[key] === u);
    if (!live.length) { toast.info("Those fixes were already undone"); return; }
    const undone = live.filter(([, u]) => u(true)).map(([key]) => key);
    setUndos((s) => Object.fromEntries(Object.entries(s).filter(([key]) => !undone.includes(key))));
    if (undone.length === live.length) toast.success("Undid all coach fixes");
    else toast.error(`Undid ${undone.length} of ${live.length} — some text was edited since. Use History to restore the rest.`);
  };
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clearPreview(), [form.subjective, form.objective, form.assessment, form.plan]);

  const ask = async (mode, text) => {
    clearPreview();
    const before = messages;
    const history = [...before, { role: "user", content: mode === "chat" ? text : "Review my note" }];
    setMessages([...history, { role: "coach", content: "" }]);
    setInput("");
    setBusy(true);
    try {
      await streamPost("/ai/coach", { note: notePayload(form), mode, messages: history.slice(-12) }, (t) =>
        setMessages([...history, { role: "coach", content: t.replace(/\*\*/g, "") }]));
    } catch (e) {
      toast.error(e.message);
      setMessages(before);
      if (mode === "chat") setInput(text);
    } finally { setBusy(false); }
  };

  const send = (e) => {
    e?.preventDefault();
    if (input.trim() && !busy) ask("chat", input.trim());
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} disabled={disabled} data-testid="coach-open-button">
        <GraduationCap className="h-4 w-4 mr-2 text-primary" />BCBA Coach
      </Button>
      <Sheet open={open} modal={!preview} onOpenChange={(v) => { if (!v) clearPreview(); setOpen(v); }}>
        <SheetContent side={preview ? "left" : "right"}
          className={`w-full flex flex-col p-0 ${preview ? "sm:max-w-sm" : "sm:max-w-xl"}`} data-testid="coach-panel"
          onInteractOutside={(e) => { if (preview || e.target?.closest?.("[data-sonner-toaster]")) e.preventDefault(); }}>
          <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <SheetTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-primary" />BCBA Coach</SheetTitle>
            <SheetDescription>Feedback on this note like a supervising BCBA would give — what's strong, what to fix, and what an auditor looks for.</SheetDescription>
          </SheetHeader>
          {preview && (
            <div className="flex items-center gap-2 border-b bg-amber-50 px-6 py-2.5 text-xs text-amber-900" data-testid="coach-preview-banner">
              <Eye className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1" data-testid="coach-preview-banner-text">
                {preview.all && preview.skipped.length === preview.entries.length ? "All fixes skipped — tap Include on one to preview it." : <>
                {preview.all ? `${preview.ok} of ${preview.entries.length - preview.skipped.length} selected fixes shown` : "Previewing"}
                {preview.sections.length ? ` in ${preview.sections.map((s) => s[0].toUpperCase() + s.slice(1)).join(", ")}` : ""}: <span className="text-red-700 line-through">red</span> is removed, <span className="text-emerald-700 font-medium">green</span> is new.
                </>}
              </span>
              {preview.all && (
                <Button size="sm" className="h-7 rounded-full px-3 text-xs" disabled={preview.entries.length === preview.skipped.length}
                  onClick={() => applyMany(preview.entries.filter(([key]) => !preview.skipped.includes(key)))} data-testid="coach-preview-apply">
                  <Sparkles className="h-3 w-3 mr-1" />Apply {preview.entries.length - preview.skipped.length}
                </Button>
              )}
              <button onClick={clearPreview} className="font-medium hover:underline" data-testid="coach-preview-close">Close</button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="coach-messages">
            {messages.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-center space-y-4" data-testid="coach-empty-state">
                <ClipboardCheck className="h-8 w-8 mx-auto text-primary" />
                <p className="text-sm text-slate-600">Get a score out of 10, specific fixes with rewritten sentences, and an insurance audit checklist.</p>
                <Button className="rounded-full" onClick={() => ask("review")} disabled={busy} data-testid="coach-review-button">Review my note</Button>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} m={m} i={i} streaming={busy && i === messages.length - 1} onApply={applyFix} applyMany={applyMany} undos={undos} setUndos={setUndos} preview={preview} onPreview={togglePreview} onPreviewAll={previewAll} onSkip={toggleSkip} dismissed={dismissed} onDismiss={dismiss} />)}
            <div ref={endRef} />
          </div>
          <div className="border-t px-6 py-4 space-y-3 bg-white">
            <div className="flex flex-wrap gap-2">
              {messages.length > 0 && (
                <button onClick={() => ask("review")} disabled={busy} className="rounded-full border px-3 py-1 text-xs text-primary hover:bg-secondary disabled:opacity-50 flex items-center gap-1" data-testid="coach-rereview-button">
                  <RotateCcw className="h-3 w-3" />Review again
                </button>
              )}
              {QUICK.map((q, i) => (
                <button key={q} onClick={() => ask("chat", q)} disabled={busy} className="rounded-full border px-3 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary disabled:opacity-50" data-testid={`coach-quick-${i}`}>{q}</button>
              ))}
            </div>
            <form onSubmit={send} className="flex gap-2">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Ask your coach anything about this note…"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} className="resize-none" data-testid="coach-input" />
              <Button type="submit" size="icon" className="h-auto w-12 rounded-xl" disabled={busy || !input.trim()} data-testid="coach-send-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
